import { pluck, contains, uniq, property } from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { Combobox as ComboboxOriginal } from '@fluentui/react-components';

export const withMultiSelectCombobox = (Combobox = ComboboxOriginal) => {
  const MultiSelectCombobox = ({
    onOptionSelect: onOptionSelectProp,
    options,
    selectedOptions,
    ...restProps
  }) => {
    const onOptionSelect = useCallback((event, data) => {
      const isInNewSelectedOptionIds = option => contains(data.selectedOptions, option.key);
      // data.selectedOptions is the list of selected options' ids.
      // We need to resolve the newly selected option to add it to the list of selected options,
      // and we need to find the newly unselected option to remove it from the list of selected options.
      const newSelectedOptions = uniq(
        [
          ...selectedOptions.filter(isInNewSelectedOptionIds),
          ...options.filter(isInNewSelectedOptionIds),
        ],
        property('key')
      );
      onOptionSelectProp(event, {
        ...data,
        resolvedSelectedOptions: newSelectedOptions,
      });
    }, [onOptionSelectProp, options, selectedOptions]);

    const selectedOptionKeys = useMemo(() => pluck(selectedOptions, 'key'), [selectedOptions]);

    // Rendering
    return (
      <Combobox
        {...restProps}
        multiselect
        freeform
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedOptionKeys}
      />
    );
  };

  MultiSelectCombobox.propTypes = {
    onOptionSelect: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })).isRequired,
    selectedOptions: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })).isRequired,
  };

  MultiSelectCombobox.displayName = `WithMultiSelectComboboxOver(${getDisplayName(Combobox)})`;
  hoistNonReactStatics(MultiSelectCombobox, Combobox);

  return MultiSelectCombobox;
};
